import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { analyticsApi } from '../../services/adminApi'

interface OverviewData {
  users: {
    current: number
    previous: number
    growth: number
    breakdown: {
      students: { current: number; previous: number; growth: number }
      teachers: { current: number; previous: number; growth: number }
    }
  }
  revenue: {
    current: number
    previous: number
    growth: number
    metrics: {
      totalOrders: number
      completedOrders: number
      totalHours: number
      avgOrderValue: number
    }
  }
  bookings: {
    current: number
    previous: number
    growth: number
    metrics: {
      completed: number
      cancelled: number
      completionRate: number
      avgRating: number
    }
  }
}

interface GrowthData {
  period: string
  new_users: number
  new_students: number
  new_teachers: number
  active_users: number
}

interface RevenueData {
  period: string
  total_orders: number
  completed_orders: number
  pending_orders: number
  failed_orders: number
  revenue: number
  hours_sold: number
  avg_order_value: number
}

export const DataAnalysis: React.FC = () => {
  const [overview, setOverview] = useState<OverviewData | null>(null)
  const [userGrowth, setUserGrowth] = useState<GrowthData[]>([])
  const [revenueData, setRevenueData] = useState<RevenueData[]>([])
  const [loading, setLoading] = useState(false)
  const [period, setPeriod] = useState<'7d' | '30d' | '90d' | '1y'>('30d')
  const [granularity, setGranularity] = useState<'hourly' | 'daily' | 'weekly' | 'monthly'>('daily')

  // 加载概览数据
  const loadOverview = async () => {
    try {
      setLoading(true)
      const response = await analyticsApi.getOverview(period)
      if (response.success) {
        setOverview(response.data.overview)
      }
    } catch (error) {
      console.error('加载概览数据失败:', error)
      toast.error('加载概览数据失败')
    } finally {
      setLoading(false)
    }
  }

  // 加载用户增长数据
  const loadUserGrowth = async () => {
    try {
      const response = await analyticsApi.getUserGrowth({ period, granularity })
      if (response.success) {
        setUserGrowth(response.data.growthTrend)
      }
    } catch (error) {
      console.error('加载用户增长数据失败:', error)
    }
  }

  // 加载收入数据
  const loadRevenueData = async () => {
    try {
      const response = await analyticsApi.getRevenue({ period, granularity })
      if (response.success) {
        setRevenueData(response.data.revenueTrend)
      }
    } catch (error) {
      console.error('加载收入数据失败:', error)
    }
  }

  // 初始化数据
  useEffect(() => {
    loadOverview()
  }, [period])

  useEffect(() => {
    loadUserGrowth()
    loadRevenueData()
  }, [period, granularity])

  // 格式化金额
  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY'
    }).format(amount)
  }

  // 格式化增长率
  const formatGrowth = (growth: number) => {
    const isPositive = growth >= 0
    const color = isPositive ? 'text-green-600' : 'text-red-600'
    const TrendIcon = isPositive ? '📈' : '📉'
    
    return (
      <div className={`flex items-center ${color}`}>
        <span className="mr-1">{TrendIcon}</span>
        <span>{Math.abs(growth).toFixed(1)}%</span>
      </div>
    )
  }

  // 简单的条形图组件
  const SimpleBarChart: React.FC<{
    data: Array<{ label: string; value: number; color?: string }>
    height?: number
  }> = ({ data, height = 200 }) => {
    const maxValue = Math.max(...data.map(d => d.value), 1)
    
    return (
      <div className="w-full" style={{ height: `${height}px` }}>
        <div className="flex items-end justify-between h-full">
          {data.map((item, index) => (
            <div key={index} className="flex-1 flex flex-col items-center">
              <div className="relative w-full flex justify-center">
                <div
                  className={`w-8/12 rounded-t ${item.color || 'bg-blue-500'}`}
                  style={{
                    height: `${(item.value / maxValue) * (height - 20)}px`
                  }}
                />
              </div>
              <div className="text-xs text-gray-600 mt-2 text-center truncate w-full">
                {item.label}
              </div>
              <div className="text-xs font-medium text-gray-900">
                {item.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // 简单的折线图组件
  const SimpleLineChart: React.FC<{
    data: Array<{ label: string; value: number }>
    height?: number
  }> = ({ data, height = 200 }) => {
    const maxValue = Math.max(...data.map(d => d.value), 1)
    const points = data.map((item, index) => {
      const x = (index / (data.length - 1 || 1)) * 100
      const y = 100 - (item.value / maxValue) * 100
      return `${x},${y}`
    }).join(' ')

    return (
      <div className="w-full relative" style={{ height: `${height}px` }}>
        <svg className="w-full h-full" viewBox="0 0 100 100">
          {/* 网格线 */}
          {[0, 25, 50, 75, 100].map(y => (
            <line
              key={y}
              x1="0"
              y1={y}
              x2="100"
              y2={y}
              stroke="#e5e7eb"
              strokeWidth="0.5"
            />
          ))}
          
          {/* 数据线 */}
          <polyline
            points={points}
            fill="none"
            stroke="#3b82f6"
            strokeWidth="2"
          />
          
          {/* 数据点 */}
          {data.map((item, index) => {
            const x = (index / (data.length - 1 || 1)) * 100
            const y = 100 - (item.value / maxValue) * 100
            return (
              <circle
                key={index}
                cx={x}
                cy={y}
                r="2"
                fill="#3b82f6"
              />
            )
          })}
        </svg>
        
        {/* X轴标签 */}
        <div className="flex justify-between text-xs text-gray-600 mt-2">
          {data.map((item, index) => (
            <div key={index} className="flex-1 text-center truncate">
              {item.label}
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 页面标题和控制 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">数据分析</h1>
          <p className="mt-2 text-gray-600">平台运营数据分析与报表</p>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* 时间周期选择 */}
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
          >
            <option value="7d">最近7天</option>
            <option value="30d">最近30天</option>
            <option value="90d">最近90天</option>
            <option value="1y">最近1年</option>
          </select>

          {/* 粒度选择 */}
          <select
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
          >
            <option value="hourly">按小时</option>
            <option value="daily">按天</option>
            <option value="weekly">按周</option>
            <option value="monthly">按月</option>
          </select>

          <button
            onClick={() => {
              loadOverview()
              loadUserGrowth()
              loadRevenueData()
            }}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <span className="mr-2">🔄</span>
            刷新数据
          </button>
        </div>
      </div>

      {/* 概览统计 */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* 用户统计 */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <span className="text-2xl mr-3">👥</span>
                <h3 className="text-lg font-semibold text-gray-900">用户增长</h3>
              </div>
              {formatGrowth(overview.users.growth)}
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">总用户数</p>
                <p className="text-2xl font-bold text-gray-900">{overview.users.current.toLocaleString()}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">学生</p>
                  <p className="text-lg font-medium text-blue-600">{overview.users.breakdown.students.current}</p>
                  {formatGrowth(overview.users.breakdown.students.growth)}
                </div>
                <div>
                  <p className="text-xs text-gray-500">教师</p>
                  <p className="text-lg font-medium text-green-600">{overview.users.breakdown.teachers.current}</p>
                  {formatGrowth(overview.users.breakdown.teachers.growth)}
                </div>
              </div>
            </div>
          </div>

          {/* 收入统计 */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <span className="text-2xl mr-3">💰</span>
                <h3 className="text-lg font-semibold text-gray-900">收入分析</h3>
              </div>
              {formatGrowth(overview.revenue.growth)}
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">总收入</p>
                <p className="text-2xl font-bold text-gray-900">{formatAmount(overview.revenue.current)}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">订单数</p>
                  <p className="text-lg font-medium text-gray-900">{overview.revenue.metrics.totalOrders}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">售出课时</p>
                  <p className="text-lg font-medium text-blue-600">{overview.revenue.metrics.totalHours}</p>
                </div>
              </div>
            </div>
          </div>

          {/* 预约统计 */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <span className="text-2xl mr-3">📅</span>
                <h3 className="text-lg font-semibold text-gray-900">课程预约</h3>
              </div>
              {formatGrowth(overview.bookings.growth)}
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">总预约数</p>
                <p className="text-2xl font-bold text-gray-900">{overview.bookings.current.toLocaleString()}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">完成率</p>
                  <p className="text-lg font-medium text-green-600">{overview.bookings.metrics.completionRate.toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">平均评分</p>
                  <p className="text-lg font-medium text-yellow-600">{overview.bookings.metrics.avgRating.toFixed(1)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 图表区域 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 用户增长趋势 */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">用户增长趋势</h3>
            <span className="text-xl">👥</span>
          </div>
          
          {userGrowth.length > 0 ? (
            <div className="space-y-4">
              <SimpleLineChart
                data={userGrowth.slice(-10).map(item => ({
                  label: new Date(item.period).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' }),
                  value: item.new_users
                }))}
                height={200}
              />
              
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">新增用户</p>
                  <p className="font-medium text-blue-600">
                    {userGrowth.reduce((sum, item) => sum + item.new_users, 0)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">新增学生</p>
                  <p className="font-medium text-green-600">
                    {userGrowth.reduce((sum, item) => sum + item.new_students, 0)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">新增教师</p>
                  <p className="font-medium text-purple-600">
                    {userGrowth.reduce((sum, item) => sum + item.new_teachers, 0)}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-500">
              暂无数据
            </div>
          )}
        </div>

        {/* 收入趋势 */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">收入分析</h3>
            <span className="text-xl">💰</span>
          </div>
          
          {revenueData.length > 0 ? (
            <div className="space-y-4">
              <SimpleLineChart
                data={revenueData.slice(-10).map(item => ({
                  label: new Date(item.period).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' }),
                  value: Math.round(item.revenue)
                }))}
                height={200}
              />
              
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">总订单</p>
                  <p className="font-medium text-gray-900">
                    {revenueData.reduce((sum, item) => sum + item.total_orders, 0)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">完成订单</p>
                  <p className="font-medium text-green-600">
                    {revenueData.reduce((sum, item) => sum + item.completed_orders, 0)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">售出课时</p>
                  <p className="font-medium text-blue-600">
                    {revenueData.reduce((sum, item) => sum + item.hours_sold, 0)}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-500">
              暂无数据
            </div>
          )}
        </div>
      </div>

      {/* 详细数据表格 */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">关键指标详情</h3>
            <span className="text-xl">📄</span>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* 用户活跃度 */}
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <span className="text-4xl text-blue-600 mx-auto mb-3 block">👥</span>
              <p className="text-xl font-bold text-gray-900 mb-1">
                {overview ? overview.users.current.toLocaleString() : '-'}
              </p>
              <p className="text-sm text-gray-600 mb-2">总用户数</p>
              <div className="text-xs text-blue-600">
                {overview ? `活跃率 ${((overview.users.breakdown.students.current / overview.users.current) * 100).toFixed(1)}%` : '-'}
              </div>
            </div>

            {/* 转化率 */}
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <span className="text-4xl text-green-600 mx-auto mb-3 block">📈</span>
              <p className="text-xl font-bold text-gray-900 mb-1">
                {overview ? `${overview.revenue.metrics.totalOrders > 0 ? ((overview.revenue.metrics.completedOrders / overview.revenue.metrics.totalOrders) * 100).toFixed(1) : 0}%` : '-'}
              </p>
              <p className="text-sm text-gray-600 mb-2">支付转化率</p>
              <div className="text-xs text-green-600">
                {overview ? `${overview.revenue.metrics.completedOrders}/${overview.revenue.metrics.totalOrders} 完成` : '-'}
              </div>
            </div>

            {/* 用户满意度 */}
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <span className="text-4xl text-purple-600 mx-auto mb-3 block">⭐</span>
              <p className="text-xl font-bold text-gray-900 mb-1">
                {overview ? `${overview.bookings.metrics.avgRating.toFixed(1)}/5.0` : '-'}
              </p>
              <p className="text-sm text-gray-600 mb-2">平均评分</p>
              <div className="text-xs text-purple-600">
                {overview ? `基于 ${overview.bookings.current} 个预约` : '-'}
              </div>
            </div>
          </div>

          {/* 数据更新时间 */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>数据更新时间</span>
              <span>{new Date().toLocaleString('zh-CN')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 数据说明 */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <span className="text-xl text-blue-600 mt-0.5 mr-3 flex-shrink-0">ℹ️</span>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">数据说明</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>所有数据均为实时统计数据，每5分钟更新一次</li>
              <li>增长率对比的是上一个周期的数据</li>
              <li>点击刷新按钮可以手动更新最新数据</li>
              <li>如需更详细的报表，可以使用自定义报表功能</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}